import React, { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Switch } from '@/components/ui/switch';
import { LogOut, Download, Loader2, Blocks, Smartphone } from 'lucide-react';
import { ProfileUsage } from './ProfileUsage';
import { ExtensionsSettings } from '@/components/extensions/ExtensionsSettings';
import { QuickSaveSettings } from '@/components/settings/QuickSaveSettings';
import { exportCanvasToJPG } from '@/lib/canvasExport';
import { useCanvasStore } from '@/stores/canvasStore';
import { usePartnrBetaStore } from '@/stores/partnrBetaStore';
import { toast } from 'sonner';
import { PartnrMark } from '@/components/partnr/kit/PartnrMark';
import { DockGlyph } from '@/components/toolbar/DockGlyph';

interface UserMenuProps {
  onOpenChange?: (open: boolean) => void;
}

export function UserMenu({ onOpenChange }: UserMenuProps = {}) {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [isExporting, setIsExporting] = useState(false);
  const [isExtensionsOpen, setIsExtensionsOpen] = useState(false);
  const [isQuickSaveOpen, setIsQuickSaveOpen] = useState(false);
  const { isPartnrBetaEnabled, setPartnrBetaEnabled } = usePartnrBetaStore();

  // Only show export option when in focus mode with items on the current board.
  // A boolean selector, so panning and edits don't re-render the menu.
  const canExport = useCanvasStore(
    (s) => s.viewMode === 'focus' && s.items.some(i => i.board_id === s.currentBoardId),
  );

  if (!user) return null;

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  const handleExport = async () => {
    setIsExporting(true);
    try {
      await exportCanvasToJPG();
      toast.success('Saved as JPG');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Export failed. Try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const userInitials = user.user_metadata?.full_name
    ? user.user_metadata.full_name.split(' ').map((n: string) => n[0]).join('').toUpperCase()
    : user.email?.charAt(0).toUpperCase() || 'U';

  return (
    <>
    <DropdownMenu onOpenChange={onOpenChange}>
      <DropdownMenuTrigger className="focus:outline-none" data-dock-item>
        <DockGlyph>
          <Avatar className="h-8 w-8 cursor-pointer ring-2 ring-transparent hover:ring-primary/20 transition-all">
            <AvatarImage 
              src={user.user_metadata?.avatar_url || user.user_metadata?.picture} 
              alt={user.user_metadata?.full_name || 'User'} 
            />
            <AvatarFallback className="bg-primary text-primary-foreground text-sm font-medium">
              {userInitials}
            </AvatarFallback>
          </Avatar>
        </DockGlyph>
      </DropdownMenuTrigger>
      <DropdownMenuContent 
        side="right" 
        align="start" 
        sideOffset={16}
        className="w-72 p-0 rounded-2xl shadow-xl border-border/50 overflow-hidden bg-background z-50"
      >
        {/* User Info Header */}
        <div className="px-5 py-4 bg-muted/30 border-b border-border/50">
          <div className="flex items-center gap-3">
            <Avatar className="h-10 w-10">
              <AvatarImage 
                src={user.user_metadata?.avatar_url || user.user_metadata?.picture} 
                alt={user.user_metadata?.full_name || 'User'} 
              />
              <AvatarFallback className="bg-primary text-primary-foreground text-sm font-medium">
                {userInitials}
              </AvatarFallback>
            </Avatar>
            <div className="flex flex-col min-w-0">
              <p className="text-sm font-semibold text-foreground truncate">
                {user.user_metadata?.full_name || 'User'}
              </p>
              <p className="text-xs text-muted-foreground truncate">
                {user.email}
              </p>
            </div>
          </div>
        </div>

        {/* Usage Section */}
        <ProfileUsage />

        {/* Partnr 1.1 Beta Toggle */}
        <div className="px-4 py-3 border-t border-border/50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <PartnrMark size={16} className="text-partnr" />
              <span className="text-sm font-medium">Partnr 1.1 Beta</span>
            </div>
            <Switch
              checked={isPartnrBetaEnabled}
              onCheckedChange={setPartnrBetaEnabled}
            />
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            Enables AI clustering & board profiles
          </p>
        </div>

        {/* Extensions (X Bookmarks lives here now) */}
        <div className="p-2 border-t border-border/50">
          <DropdownMenuItem
            onClick={() => setIsExtensionsOpen(true)}
            className="cursor-pointer rounded-xl h-10"
          >
            <Blocks className="mr-2 h-4 w-4" />
            Extensions
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => setIsQuickSaveOpen(true)}
            className="cursor-pointer rounded-xl h-10"
          >
            <Smartphone className="mr-2 h-4 w-4" />
            Save from iPhone
          </DropdownMenuItem>
        </div>

        {/* Export Option - only in focus mode with items */}
        {canExport && (
          <div className="p-2 border-t border-border/50">
            <DropdownMenuItem 
              onClick={handleExport}
              disabled={isExporting}
              className="cursor-pointer rounded-xl h-10"
            >
              {isExporting ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Download className="mr-2 h-4 w-4" />
              )}
              Export as JPG
            </DropdownMenuItem>
          </div>
        )}

        {/* Sign Out */}
        <div className="p-2 border-t border-border/50">
          <DropdownMenuItem 
            onClick={handleSignOut} 
            className="text-destructive focus:bg-destructive focus:text-white cursor-pointer rounded-xl h-10"
          >
            <LogOut className="mr-2 h-4 w-4" />
            Sign out
          </DropdownMenuItem>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>

    <ExtensionsSettings open={isExtensionsOpen} onOpenChange={setIsExtensionsOpen} />
    <QuickSaveSettings open={isQuickSaveOpen} onOpenChange={setIsQuickSaveOpen} />
    </>
  );
}
